import { forwardRef, type ComponentPropsWithoutRef, type ComponentRef } from 'react';
import * as AvatarPrimitive from '@radix-ui/react-avatar';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@r/utils/utils.ts';

const avatarVariants = cva('relative flex shrink-0 overflow-hidden rounded-full aspect-square', {
  variants: {
    size: {
      default: 'w-20',
      nav: 'w-10',
      sm: 'w-[3.75rem]',
      lg: 'w-[6.25rem]'
    }
  },
  defaultVariants: {
    size: 'default'
  }
});

export interface AvatarProps
  extends ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>,
    VariantProps<typeof avatarVariants> {}

const Avatar = forwardRef<ComponentRef<typeof AvatarPrimitive.Root>, AvatarProps>(
  ({ className, size, ...props }, ref) => (
    <AvatarPrimitive.Root
      ref={ref}
      className={cn(avatarVariants({ size, className }))}
      {...props}
    />
  )
);

Avatar.displayName = AvatarPrimitive.Root.displayName;

const AvatarImage = forwardRef<
  ComponentRef<typeof AvatarPrimitive.Image>,
  ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Image
    ref={ref}
    className={cn('object-cover object-center w-full', className)}
    {...props}
  />
));

AvatarImage.displayName = AvatarPrimitive.Image.displayName;

const AvatarFallbackVariants = cva(
  'flex h-full w-full items-center justify-center rounded-full bg-foreground text-primary-foreground text-xl',
  {
    variants: {
      fontSize: {
        default: 'text-2xl',
        nav: 'text-sm',
        sm: 'text-xl',
        lg: 'text-3xl'
      }
    },
    defaultVariants: {
      fontSize: 'default'
    }
  }
);

export interface AvatarFallbackProps
  extends ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>,
    VariantProps<typeof AvatarFallbackVariants> {
  text?: string;
  backgroundColor?: string;
  color?: string;
  fontSize?: 'default' | 'nav' | 'sm' | 'lg';
  initials?: boolean;
}

function getInitials(input: string): string {
  return input
    .split(/\s+/) // Split by any whitespace
    .map((word) => word.charAt(0)) // Get the first character of each word
    .join('') // Join them together
    .replace(/[^a-zA-Z0-9]/g, '') // Remove any non-alphanumeric characters
    .slice(0, 2); // Limit to 2 characters
}

const AvatarFallback = forwardRef<
  ComponentRef<typeof AvatarPrimitive.Fallback>,
  AvatarFallbackProps
>(({ className, text, backgroundColor, color, fontSize, initials = false, ...props }, ref) => (
  <AvatarPrimitive.Fallback
    ref={ref}
    className={cn(AvatarFallbackVariants({ fontSize, className }))}
    style={{ backgroundColor, color }}
    {...props}
  >
    {initials ? getInitials(text || '') : text}
  </AvatarPrimitive.Fallback>
));

AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

export { Avatar, AvatarImage, AvatarFallback };
